import React, { useState, useContext, useEffect, memo } from 'react'
import { CircularProgress, styled } from '@mui/material'
import { getSession, updateSession, logout } from '../api'
import { FeedbackContext } from '../feedback'
import { LayoutProvider } from '../layout'
import ExternApp from './ExternApp'
import InternLayout from './InternLayout'
import { AppContext } from './AppContext'
import { SessionHolderProps } from './SessionHolder.types'

const Container = styled('div')(({ theme }) => ({
    display: 'flex',
    justifyContent: 'center',
    alignContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
}))

function SessionHolder({ routes }: SessionHolderProps) {
    const context = useContext(AppContext)
    const feedbackContext = useContext(FeedbackContext)

    const [status, setStatus] = useState<
        'unknown' | 'out' | 'in' | 'switching'
    >('unknown')
    const [completed, setCompleted] = useState<number>(0)

    context.removeSession = () => {
        setStatus('switching')
        feedbackContext.showBackdrop()
        logout()
            .then(() => {
                setStatus('out')
                context.setSession(undefined)
            })
            .catch((error) => {
                feedbackContext.showError(error.message)
                setStatus('in')
            })
            .finally(() => {
                feedbackContext.closeBackdrop()
            })
    }

    context.onSignedIn = () => {
        setCompleted(new Date().getTime())
    }

    context.onChangedTenant = (tenantId: string) => {
        setStatus('switching')
        feedbackContext.showBackdrop()
        updateSession(tenantId)
            .then((session) => {
                window.location.href = '/'
            })
            .catch((error) => {
                feedbackContext.showError(error.message)
                setStatus('in')
            })
            .finally(() => {
                feedbackContext.closeBackdrop()
            })
    }

    useEffect(() => {
        getSession()
            .then((session) => {
                context.setSession(session)
                setStatus('in')
            })
            .catch((error) => {
                context.setSession(undefined)
                setStatus('out')
            })
        // eslint-disable-next-line
    }, [completed])

    if (status === 'unknown' || status === 'switching') {
        return (
            <Container>
                <CircularProgress
                    sx={(theme) => {
                        return { margin: theme.spacing(4) }
                    }}
                />
            </Container>
        )
    }
    if (status === 'out') {
        return <ExternApp />
    }

    return (
        <LayoutProvider>
            <InternLayout routes={routes} />
        </LayoutProvider>
    )
}

export default memo(SessionHolder)
